import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hospital } from "lucide-react";

export default function TermsAndConditions() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="rounded-lg bg-blue-600 p-2">
              <Hospital className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">AMCARE</h1>
          </Link>
          <Button variant="outline" asChild>
            <Link to="/login">Login</Link>
          </Button>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Terms & Conditions</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <p className="text-gray-700 mb-4">
                Please read these Terms and Conditions ("Terms") carefully before using AMCare's 
                healthcare management SaaS platform. By accessing or using our service, you agree 
                to be bound by these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-700">
                By registering for or using AMCare services, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms and our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Service Description</h2>
              <p className="text-gray-700 mb-2">
                AMCare provides a cloud-based healthcare management platform including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Patient registration and management</li>
                <li>Queue management system</li>
                <li>E-prescription generation</li>
                <li>Billing and payment processing</li>
                <li>Medical records and history tracking</li>
                <li>Analytics and reporting</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. User Accounts and Responsibilities</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold mb-2">Account Security</h3>
                  <p className="text-gray-700">
                    You are responsible for maintaining the confidentiality of your account credentials 
                    and for all activities that occur under your account.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Accurate Information</h3>
                  <p className="text-gray-700">
                    You agree to provide accurate, current, and complete information during registration 
                    and to update such information to keep it accurate and current.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Subscription and Payment</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Subscription fees are charged in advance based on your selected plan</li>
                <li>All fees are non-refundable except as specified in our Refund Policy</li>
                <li>We reserve the right to change pricing with 30 days' notice</li>
                <li>Subscriptions automatically renew unless cancelled</li>
                <li>You are responsible for any applicable taxes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Acceptable Use</h2>
              <p className="text-gray-700 mb-2">You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Transmit any malicious code or viruses</li>
                <li>Attempt to gain unauthorized access to the system</li>
                <li>Interfere with or disrupt the service</li>
                <li>Share your account credentials with others</li>
                <li>Use the service to store or process data in violation of healthcare regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Data and Privacy</h2>
              <p className="text-gray-700 mb-2">
                You retain ownership of all data you submit to the platform. By using our service, you:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Grant us a license to use your data to provide the service</li>
                <li>Confirm you have necessary rights to submit patient data</li>
                <li>Agree to comply with all applicable healthcare data protection laws</li>
                <li>Understand our data handling practices as described in our Privacy Policy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Intellectual Property</h2>
              <p className="text-gray-700">
                The AMCare platform, including its software, design, and content, is protected by 
                intellectual property laws. You may not copy, modify, or create derivative works 
                without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Service Availability</h2>
              <p className="text-gray-700">
                We strive to maintain high service availability but do not guarantee uninterrupted 
                access. We may perform maintenance that temporarily affects service availability. 
                We are not liable for any downtime or service interruptions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Limitation of Liability</h2>
              <p className="text-gray-700">
                AMCare provides the service "as is" without warranties. We are not liable for any 
                indirect, incidental, or consequential damages arising from your use of the service. 
                Our total liability is limited to the amount you paid in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Termination</h2>
              <p className="text-gray-700 mb-2">
                We may suspend or terminate your account if you:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Violate these Terms</li>
                <li>Fail to pay subscription fees</li>
                <li>Engage in fraudulent or illegal activities</li>
              </ul>
              <p className="text-gray-700 mt-3">
                You may cancel your subscription at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify these Terms at any time. We will notify you of 
                significant changes via email or through the platform. Continued use after changes 
                constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Governing Law</h2>
              <p className="text-gray-700">
                These Terms are governed by the laws of India. Any disputes shall be subject to 
                the exclusive jurisdiction of courts in India.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">13. Contact Us</h2>
              <p className="text-gray-700 mb-2">
                For questions about these Terms, please contact us:
              </p>
              <div className="text-gray-700">
                <p>Email: <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">support@amcares.in</a></p>
                <p className="mt-2">Visit: <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us Page</a></p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>&copy; {new Date().getFullYear()} AMCare. All rights reserved.</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
            <Link to="/contact-us" className="hover:underline">Contact Us</Link>
            <Link to="/refund-policy" className="hover:underline">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

